'use client'

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { UsageLimitModal } from '@/components/billing/UsageLimitModal'

export interface UsageLimitDetails {
  eventType: string
  creditsRequired: number
  creditsAvailable: number
  upgradeUrl: string
}

export interface PlanRequiredDetails {
  eventType: string
  currentPlan: string
  requiredPlan: string
  upgradeUrl: string
}

export type LimitModalDetails =
  | { kind: 'usage_limit_exceeded'; details: UsageLimitDetails }
  | { kind: 'plan_required'; details: PlanRequiredDetails }

interface UsageLimitHandler {
  showUsageLimit: (details: UsageLimitDetails) => void
  showPlanRequired: (details: PlanRequiredDetails) => void
  hideUsageLimit: () => void
}

const UsageLimitContext = createContext<UsageLimitHandler | null>(null)

function isUsageLimitDetails(value: unknown): value is UsageLimitDetails {
  if (!value || typeof value !== 'object') return false
  const details = value as Record<string, unknown>
  return (
    typeof details.eventType === 'string' &&
    typeof details.creditsRequired === 'number' &&
    typeof details.creditsAvailable === 'number' &&
    typeof details.upgradeUrl === 'string'
  )
}

function isPlanRequiredDetails(value: unknown): value is PlanRequiredDetails {
  if (!value || typeof value !== 'object') return false
  const details = value as Record<string, unknown>
  return (
    typeof details.eventType === 'string' &&
    typeof details.currentPlan === 'string' &&
    typeof details.requiredPlan === 'string' &&
    typeof details.upgradeUrl === 'string'
  )
}

type ParsedLimitPayload =
  | { kind: 'usage_limit_exceeded'; message?: string; details: UsageLimitDetails }
  | { kind: 'plan_required'; message?: string; details: PlanRequiredDetails }

async function readLimitPayload(res: Response): Promise<ParsedLimitPayload | null> {
  try {
    const data = await res.clone().json() as {
      error?: unknown
      message?: unknown
      details?: unknown
    }

    if (data.error === 'usage_limit_exceeded' && isUsageLimitDetails(data.details)) {
      return {
        kind: 'usage_limit_exceeded',
        message: typeof data.message === 'string' ? data.message : undefined,
        details: data.details,
      }
    }

    if (data.error === 'plan_required' && isPlanRequiredDetails(data.details)) {
      return {
        kind: 'plan_required',
        message: typeof data.message === 'string' ? data.message : undefined,
        details: data.details,
      }
    }
  } catch {
    // Not JSON or already unreadable; caller handles the response normally.
  }

  return null
}

export function UsageLimitProvider({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<LimitModalDetails | null>(null)
  const [open, setOpen] = useState(false)

  const showUsageLimit = useCallback((nextDetails: UsageLimitDetails) => {
    setModal({ kind: 'usage_limit_exceeded', details: nextDetails })
    setOpen(true)
  }, [])

  const showPlanRequired = useCallback((nextDetails: PlanRequiredDetails) => {
    setModal({ kind: 'plan_required', details: nextDetails })
    setOpen(true)
  }, [])

  const hideUsageLimit = useCallback(() => {
    setOpen(false)
  }, [])

  const handler = useMemo(() => ({
    showUsageLimit,
    showPlanRequired,
    hideUsageLimit,
  }), [hideUsageLimit, showPlanRequired, showUsageLimit])

  useEffect(() => {
    const originalFetch = window.fetch

    window.fetch = async (...args) => {
      const response = await originalFetch(...args)

      if (response.status === 402) {
        const payload = await readLimitPayload(response)
        if (payload?.kind === 'usage_limit_exceeded') {
          showUsageLimit(payload.details)
        } else if (payload?.kind === 'plan_required') {
          showPlanRequired(payload.details)
        }
      }

      return response
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [showPlanRequired, showUsageLimit])

  return createElement(
    UsageLimitContext.Provider,
    { value: handler },
    children,
    createElement(UsageLimitModal, { open, modal, onOpenChange: setOpen }),
  )
}

export function useUsageLimitHandler(): UsageLimitHandler {
  const context = useContext(UsageLimitContext)
  if (!context) {
    throw new Error('useUsageLimitHandler must be used within UsageLimitProvider')
  }
  return context
}

export async function handleApiResponse<T>(
  res: Response,
  handler: {
    showUsageLimit: (details: UsageLimitDetails) => void
    showPlanRequired?: (details: PlanRequiredDetails) => void
  },
): Promise<T> {
  if (res.status === 402) {
    const payload = await readLimitPayload(res)
    if (payload?.kind === 'usage_limit_exceeded') {
      handler.showUsageLimit(payload.details)
      throw new Error(payload.message || 'Usage limit exceeded')
    }
    if (payload?.kind === 'plan_required') {
      handler.showPlanRequired?.(payload.details)
      throw new Error(payload.message || 'Plan upgrade required')
    }
  }

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || 'Request failed')
  }

  return res.json() as Promise<T>
}
